import React, { useEffect, useMemo } from 'react';
import T from 'i18n';
import {
  MenuItem,
  Menu,
  Typography,
  IconButton,
  TextField,
  Tooltip,
  CircularProgress,
  Box,
} from '@mui/material';
import { useFetchSearchHistory } from '../redux/fetchSearchHistory';
import { Spinner } from 'features/common';
import {
  Delete,
  Edit,
  Group,
  Save,
  Clear,
  Psychology,
  Memory as MemoryIcon,
  VerticalSplit as VerticalSplitIcon,
} from '@mui/icons-material';
import { useUpdateSearchHistoryItem } from '../redux/updateSearchHistoryItem';
import { useDeleteSearchHistoryItem } from '../redux/deleteSearchHistoryItem';
import { groupByDateLabel, groupDataByDate } from 'utils/groupDataByDate';
import { removeFormatSuffix } from './utils/formatUtils';
import { useToasts } from 'react-toast-notifications';

interface SearchHistoryProps {
  open: boolean;
  anchorEl: any;
  handleSearchHistoryClose: any;
  handleSearchHistoryMenuItemClick: (itm: any) => void;
  folderId?: string;
  fileId?: string;
  fileSidebarAskAI?: boolean;
  searchHistory: any;
  // Defaults preserve existing SearchCenter behavior
  useReduxSearchHistory?: boolean;
  loading?: boolean;
  inlineLoading?: boolean;
  showActions?: boolean;
  truncateTitles?: boolean;
  anchorOrigin?: any;
  transformOrigin?: any;
  onRename?: (item: any, newName: string) => Promise<any>;
  onDelete?: (item: any) => Promise<any>;
}

const SearchHistory = ({
  open,
  anchorEl,
  handleSearchHistoryClose,
  handleSearchHistoryMenuItemClick,
  searchHistory: searchHistoryProp,
  useReduxSearchHistory = true,
  loading,
  inlineLoading,
  showActions = true,
  truncateTitles = false,
  anchorOrigin,
  transformOrigin,
  onRename,
  onDelete,
}: SearchHistoryProps) => {
  const { searchHistory: searchHistoryOriginal, fetchSearchHistoryPending } =
    useFetchSearchHistory();
  const { updateSearchHistoryItem: updateSearchHistoryItemAction } = useUpdateSearchHistoryItem();
  const { deleteSearchHistoryItem } = useDeleteSearchHistoryItem();
  const [selectedItem, setSelectedItem] = React.useState<any>(null);
  const [editValue, setEditValue] = React.useState('');
  const [updatingItemId, setUpdatingItemId] = React.useState<string | null>(null);
  const [searchHistory, setSearchHistory] = React.useState<any>(searchHistoryProp || {});
  const [filterText, setFilterText] = React.useState<string>('');
  const menuListRef = React.useRef<HTMLUListElement | null>(null);
  const skipBlurOnCancelRef = React.useRef(false);
  const [deletingItemId, setDeletingItemId] = React.useState<string | null>(null);
  const { addToast } = useToasts();

  const updateItem = () => {
    if (!selectedItem) return;
    const trimmed = editValue.trim();
    const original = removeFormatSuffix(selectedItem.title);
    if (!trimmed || trimmed === original || updatingItemId) return;
    setUpdatingItemId(selectedItem.id);

    let p;
    if (onRename) {
      p = onRename(selectedItem, trimmed);
    } else {
      p = updateSearchHistoryItemAction({
        searchHistoryItem: { ...selectedItem, title: trimmed },
      });
    }

    Promise.resolve(p)
      .then(() => {
        addToast(
          T.translate('case.updateSearchHistoryItemSuccess') || 'Search history item updated.',
          {
            appearance: 'success',
            autoDismiss: true,
          },
        );
      })
      .finally(() => {
        setUpdatingItemId(null);
        setSelectedItem(null);
        // Restore focus to menu list so visual styles relying on focus (e.g. box-shadow) remain
        requestAnimationFrame(() => {
          if (menuListRef.current) {
            menuListRef.current.focus();
          }
        });
      });
  };

  const handleDelete = (searchHistoryItm: any) => {
    if (deletingItemId) return; // prevent parallel deletes
    setDeletingItemId(searchHistoryItm.id);

    let p;
    if (onDelete) {
      p = onDelete(searchHistoryItm);
    } else {
      p = deleteSearchHistoryItem({ searchHistoryItm });
    }

    Promise.resolve(p)
      .then(() => {
        addToast(T.translate('case.deleteSearchHistoryItemSuccess') || 'Deleted.', {
          appearance: 'success',
          autoDismiss: true,
        });
      })
      .catch(() => {
        addToast(T.translate('feedback.deleteSearchHistoryItemFailure') || 'Delete failed.', {
          appearance: 'error',
          autoDismiss: true,
        });
      })
      .finally(() => {
        setDeletingItemId(null);
        // Restore focus to retain menu paper shadow styling
        requestAnimationFrame(() => {
          if (menuListRef.current) menuListRef.current.focus();
        });
      });
  };

  const areAllGroupsEmpty = (groups: any) => {
    return Object.values(groups).every((group: any) => group.length === 0);
  };

  const getTotalItemsCount = (groups: any) => {
    try {
      return Object.values(groups).reduce(
        (sum: number, group: any) => sum + (Array.isArray(group) ? group.length : 0),
        0,
      );
    } catch {
      return 0;
    }
  };

  const filteredSearchHistory = useMemo(() => {
    if (!filterText.trim()) return searchHistory;
    const query = filterText.toLowerCase();
    const result: any = {};
    try {
      Object.entries(searchHistory || {}).forEach(([groupKey, items]: any) => {
        const filteredItems = (items || []).filter((itm: any) => {
          const nameText = (itm?.title || '').toLowerCase();
          const queryText = (itm?.query || '').toLowerCase();
          return nameText.includes(query) || queryText.includes(query);
        });
        result[groupKey] = filteredItems;
      });
      return result;
    } catch {
      return searchHistory;
    }
  }, [searchHistory, filterText]);

  const effectiveSearchHistory = filterText.trim() ? filteredSearchHistory : searchHistory;
  const totalItems = useMemo(
    () => getTotalItemsCount(effectiveSearchHistory),
    [effectiveSearchHistory],
  );
  // Update local state when prop changes
  useEffect(() => {
    setSearchHistory(searchHistoryProp || {});
  }, [searchHistoryProp]);

  // Keep searchHistoryOriginal sync for updates/deletes
  useEffect(() => {
    if (!useReduxSearchHistory) return;
    if (searchHistoryOriginal && searchHistoryOriginal.length > 0) {
      const formattedData = groupDataByDate(searchHistoryOriginal);
      setSearchHistory(formattedData);
    }
  }, [searchHistoryOriginal, useReduxSearchHistory]);
  useEffect(() => {
    if (open) {
      setFilterText('');
    }
  }, [open]);
  const isLoading = useReduxSearchHistory ? fetchSearchHistoryPending : !!loading;
  const showInlineLoader = !isLoading && !!inlineLoading;
  return (
    <Menu
      id="search-history-menu"
      anchorEl={anchorEl}
      open={open}
      onClose={handleSearchHistoryClose}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      slotProps={{
        paper: {
          elevation: 1,
          sx: {
            borderRadius: '4px',
            transition: '120ms ease-in-out',
            marginTop: '8px',
          },
        },
      }}
      MenuListProps={{
        'aria-labelledby': 'basic-button',
        // Make list programmatically focusable to re-apply focus styles (shadow) after edits
        ref: menuListRef as any,
        tabIndex: -1,
        //sx: { height: '60vh', width: '30vw' },
      }}
    >
      {isLoading ? (
        <MenuItem sx={{ width: '30vw', justifyContent: 'center' }}>
          <Spinner />
        </MenuItem>
      ) : (
        <>
          {totalItems > 0 && (
            <MenuItem
              disableRipple
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              sx={{
                width: '30vw',
                backgroundColor: (theme) => theme.palette.background.paper,
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.background.paper,
                },
                '&.Mui-focusVisible': {
                  backgroundColor: (theme) => theme.palette.background.paper,
                },
                '&.Mui-selected': {
                  backgroundColor: (theme) => theme.palette.background.paper,
                },
                '&.MuiMenuItem-root': {
                  backgroundColor: (theme) => theme.palette.background.paper,
                },
              }}
            >
              <TextField
                autoFocus={!selectedItem}
                size="small"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                placeholder={T.translate('case.searchHistoryFilterPlaceholder') || 'Search history'}
                fullWidth
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                InputProps={{
                  endAdornment: filterText ? (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setFilterText('');
                      }}
                      aria-label={T.translate('generic.clear') || 'Clear'}
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  ) : null,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#d1d5db',
                    borderWidth: '1px',
                  },
                  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#999999',
                    borderWidth: '1px',
                  },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#999999',
                    borderWidth: '1px',
                  },
                }}
              />
            </MenuItem>
          )}
          {/* Replaced PerfectScrollbar with native scroll container to ensure visibility inside MUI Menu */}
          <Box
            sx={{
              maxHeight: '60vh',
              height: '60vh',
              minWidth: '25vw',
              overflowY: totalItems <= 8 ? 'hidden' : 'auto',
              overflowX: 'hidden',
              position: 'relative',
              paddingRight: '4px',
              scrollbarGutter: 'stable',
              scrollbarWidth: 'thin',
              scrollbarColor: 'transparent transparent',
              '&::-webkit-scrollbar': {
                width: '6px',
                height: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'transparent',
                borderRadius: '8px',
              },
              '&:hover': {
                scrollbarColor: 'rgba(0, 0, 0, 0.35) transparent',
              },
              '&:hover::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0, 0, 0, 0.35)',
              },
            }}
          >
            {showInlineLoader ? (
              <MenuItem sx={{ width: '30vw', justifyContent: 'center' }} disabled>
                <Spinner />
              </MenuItem>
            ) : null}
            {effectiveSearchHistory && !areAllGroupsEmpty(effectiveSearchHistory) ? (
              Object.entries(effectiveSearchHistory).map(
                ([key, value]: any, index: number) =>
                  value.length > 0 && (
                    <React.Fragment key={index}>
                      <MenuItem disabled>
                        <Typography variant="subtitle1">{groupByDateLabel(key)}</Typography>
                      </MenuItem>
                      {value.map((searchHistoryItm: any, indexV: number) => (
                        <MenuItem
                          key={indexV}
                          // Disable selection action while editing this specific item so the menu doesn't close
                          onClick={
                            showActions && selectedItem && selectedItem.id === searchHistoryItm.id
                              ? undefined
                              : () => handleSearchHistoryMenuItemClick(searchHistoryItm)
                          }
                          sx={{
                            justifyContent: 'space-between',
                            width: '30vw',
                            '&.Mui-focusVisible': {
                              backgroundColor: 'transparent',
                            },
                          }}
                        >
                          {showActions &&
                          selectedItem &&
                          selectedItem.id === searchHistoryItm.id ? (
                            <>
                              <TextField
                                autoFocus
                                size="small"
                                value={editValue}
                                placeholder={T.translate('case.searchHistoryItemPlaceholder')}
                                required
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                                onChange={(e: any) => setEditValue(e.target.value)}
                                onBlur={() => {
                                  if (skipBlurOnCancelRef.current) {
                                    skipBlurOnCancelRef.current = false;
                                    return;
                                  }
                                  if (updatingItemId) return; // prevent duplicate while updating
                                  updateItem();
                                }}
                                onKeyDown={(e: any) => {
                                  if (updatingItemId) return; // disabled during update
                                  if (e.key === 'Enter' && editValue.trim()) {
                                    updateItem();
                                  }
                                }}
                                sx={{ width: '25vw' }}
                              />
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Tooltip title="Cancel">
                                  <IconButton
                                    size="small"
                                    aria-label="Cancel"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setSelectedItem(null);
                                      setEditValue('');
                                    }}
                                    onMouseDown={(e) => {
                                      skipBlurOnCancelRef.current = true;
                                      // Prevent Menu from treating this as an outside click or item selection
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }}
                                    sx={{ minWidth: 0 }}
                                  >
                                    <Clear fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Save">
                                  <span>
                                    <IconButton
                                      size="small"
                                      aria-label="Save"
                                      disabled={
                                        updatingItemId === selectedItem.id ||
                                        !editValue.trim() ||
                                        editValue.trim() === removeFormatSuffix(selectedItem.title)
                                      }
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        updateItem();
                                      }}
                                      onMouseDown={(e) => {
                                        // Prevent Menu from treating this as an outside click or item selection
                                        e.preventDefault();
                                        e.stopPropagation();
                                      }}
                                      sx={{ minWidth: 0 }}
                                    >
                                      {updatingItemId === selectedItem.id ? (
                                        <CircularProgress size={16} />
                                      ) : (
                                        <Save fontSize="small" />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </div>
                            </>
                          ) : (
                            <>
                              {showActions ? (
                                <>
                                  {searchHistoryItm.command === 'answers-direct' ||
                                  searchHistoryItm.usesOnlyCache ||
                                  searchHistoryItm.source === 'sidebar' ? (
                                    <Tooltip
                                      title={
                                        <>
                                          {searchHistoryItm.command === 'answers-direct' ? (
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                              <Psychology
                                                fontSize="small"
                                                sx={{
                                                  mr: 0.5,
                                                  verticalAlign: 'middle',
                                                  color: 'GrayText',
                                                }}
                                              />
                                              <Typography variant="caption">
                                                {T.translate('generic.answerDirectInfo')}
                                              </Typography>
                                            </div>
                                          ) : null}
                                          {searchHistoryItm.usesOnlyCache ? (
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                              <MemoryIcon
                                                fontSize="small"
                                                sx={{
                                                  mr: 0.5,
                                                  verticalAlign: 'middle',
                                                  color: '#7BA0F4',
                                                }}
                                              />
                                              <Typography variant="caption">
                                                {T.translate('generic.useCachedInfo')}
                                              </Typography>
                                            </div>
                                          ) : null}
                                          {searchHistoryItm.source === 'sidebar' ? (
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                              <VerticalSplitIcon
                                                fontSize="small"
                                                sx={{
                                                  mr: 0.5,
                                                  verticalAlign: 'middle',
                                                  color: '#EF8E43',
                                                }}
                                              />
                                              <Typography variant="caption">
                                                {T.translate('generic.sidebarInfo')}
                                              </Typography>
                                            </div>
                                          ) : null}
                                        </>
                                      }
                                      placement="top-start"
                                    >
                                      <Typography
                                        sx={{
                                          width: '25vw',
                                          ...(truncateTitles
                                            ? {
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                              }
                                            : {
                                                wordWrap: 'break-word',
                                                whiteSpace: 'normal',
                                                textOverflow: 'clip',
                                              }),
                                        }}
                                      >
                                        {searchHistoryItm.command === 'answers-direct' ? (
                                          <Psychology
                                            fontSize="small"
                                            sx={{
                                              mr: 0.5,
                                              verticalAlign: 'middle',
                                              color: 'GrayText',
                                            }}
                                          />
                                        ) : null}
                                        {searchHistoryItm.usesOnlyCache ? (
                                          <MemoryIcon
                                            fontSize="small"
                                            sx={{
                                              mr: 0.5,
                                              verticalAlign: 'middle',
                                              color: '#7BA0F4',
                                            }}
                                          />
                                        ) : null}
                                        {searchHistoryItm.source === 'sidebar' ? (
                                          <VerticalSplitIcon
                                            fontSize="small"
                                            sx={{
                                              mr: 0.5,
                                              verticalAlign: 'middle',
                                              color: '#EF8E43',
                                            }}
                                          />
                                        ) : null}
                                        {removeFormatSuffix(searchHistoryItm.title)}
                                      </Typography>
                                    </Tooltip>
                                  ) : (
                                    <Typography
                                      sx={{
                                        width: '25vw',
                                        ...(truncateTitles
                                          ? {
                                              whiteSpace: 'nowrap',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                            }
                                          : {
                                              wordWrap: 'break-word',
                                              whiteSpace: 'normal',
                                              textOverflow: 'clip',
                                            }),
                                      }}
                                    >
                                      {removeFormatSuffix(searchHistoryItm.title)}
                                    </Typography>
                                  )}
                                  <div>
                                    {searchHistoryItm.contributors &&
                                      searchHistoryItm.contributors.length > 0 && (
                                        <Tooltip
                                          title={searchHistoryItm.contributors
                                            .map((itm: any) => itm.title)
                                            .join(', ')}
                                          placement="top"
                                          arrow
                                        >
                                          <Group
                                            fontSize="small"
                                            sx={{ cursor: 'default', color: 'GrayText' }}
                                          />
                                        </Tooltip>
                                      )}
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setSelectedItem(searchHistoryItm);
                                        setEditValue(removeFormatSuffix(searchHistoryItm.title));
                                      }}
                                    >
                                      <Edit fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      disabled={!!deletingItemId}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (selectedItem) setSelectedItem(null);
                                        handleDelete(searchHistoryItm);
                                      }}
                                      sx={{
                                        minWidth: 0,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                      }}
                                    >
                                      {deletingItemId === searchHistoryItm.id ? (
                                        <>
                                          <CircularProgress size={14} sx={{ mr: 0.5 }} />
                                          <Typography variant="caption">
                                            {T.translate('case.deletingInProgress') || 'Deleting..'}
                                          </Typography>
                                        </>
                                      ) : (
                                        <Delete fontSize="small" />
                                      )}
                                    </IconButton>
                                  </div>
                                </>
                              ) : (
                                <Tooltip
                                  title={removeFormatSuffix(searchHistoryItm.title)}
                                  placement="top-start"
                                  disableHoverListener={!truncateTitles}
                                >
                                  <Typography
                                    sx={{
                                      width: '25vw',
                                      ...(truncateTitles
                                        ? {
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                          }
                                        : {
                                            wordWrap: 'break-word',
                                            whiteSpace: 'normal',
                                            textOverflow: 'clip',
                                          }),
                                    }}
                                  >
                                    {removeFormatSuffix(searchHistoryItm.title)}
                                  </Typography>
                                </Tooltip>
                              )}
                            </>
                          )}
                        </MenuItem>
                      ))}
                    </React.Fragment>
                  ),
              )
            ) : (
              <MenuItem disabled>
                {filterText.trim()
                  ? T.translate('generic.noResults') || 'No results'
                  : T.translate('case.noSearchHistory')}
              </MenuItem>
            )}
          </Box>
        </>
      )}
    </Menu>
  );
};

export default SearchHistory;
